import React, { useState, useEffect, useRef } from 'react';

const timelineItems = [
  {
    year: '1905',
    title: 'The Foundation',
    caption: 'I.M. Fernandes & Sons',
    img: '/images/1905.webp',
  },
  {
    year: '2013',
    title: 'The Reinvention',
    caption: 'Studio Fernandes launches',
    img: '/images/store2.webp',
  },
  {
    year: '2026',
    title: 'The Expansion',
    caption: 'A modern bespoke vision',
    img: '/images/store1.webp',
  },
];

const founders = [
  { name: 'Mozart Fernandes', role: '', img: '/images/founder-mozart.webp' },
  { name: 'Miskha Fernandes', role: '', img: '/images/mishka-founder.webp' },
  { name: 'Myron Fernandes', role: '', img: '/images/cp-founder.webp' },
];

const OurStory = () => {
  const [active, setActive] = useState(0);
  const sectionRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    const observers = [];
    let timeout;

    timeout = setTimeout(() => {
      sectionRefs.current.forEach((el, i) => {
        if (!el) return;
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) setActive(i);
          },
          { threshold: 0.6 }
        );
        observer.observe(el);
        observers.push(observer);
      });
    }, 100);

    return () => {
      clearTimeout(timeout);
      observers.forEach((o) => o.disconnect());
    };
  }, []);

  const scrollToSection = (i) => {
    sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Mobile hero — only visible below lg */}
      <header className="lg:hidden w-full pt-12 pb-6 px-8 bg-background-light dark:bg-background-dark">
        <span className="text-[10px] uppercase tracking-[0.5em] text-primary mb-6 block">Est. 1905</span>
        <h1 className="font-display text-5xl leading-[0.95] mb-6 dark:text-white">Our<br />Story</h1>
        <div className="w-10 h-px bg-primary mb-6" />
        <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xs leading-relaxed">Four generations of tailoring excellence. One modern vision.</p>
      </header>

      {/* Split-screen — sticky left timeline, scrollable right images */}
      <section className="flex flex-col lg:flex-row bg-background-light dark:bg-background-dark">

        {/* Left — sticky vertical timeline with hero text at top */}
        <div className="hidden lg:flex w-[35%] sticky top-0 h-screen flex-col px-12 xl:px-16 border-r border-gray-200 dark:border-gray-700 flex-shrink-0 pt-12">
          <span className="text-[10px] uppercase tracking-[0.5em] text-primary mb-4 block">Est. 1905</span>
          <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-4 dark:text-white">Our<br />Story</h1>
          <div className="w-10 h-px bg-primary mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xs leading-relaxed mb-10">Four generations of tailoring excellence. One modern vision.</p>
          <p className="text-[10px] uppercase tracking-[0.5em] text-primary mb-10">Our History</p>

          <div className="relative">
            <div className="absolute left-[7px] top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />

            {timelineItems.map((item, i) => (
              <button
                key={item.year}
                onClick={() => scrollToSection(i)}
                className="relative flex items-start gap-6 mb-20 last:mb-0 w-full text-left group"
              >
                <div className={`relative z-10 w-4 h-4 rounded-full border-2 flex-shrink-0 mt-1 transition-all duration-500 ${
                  active === i
                    ? 'bg-primary border-primary scale-125'
                    : 'bg-white dark:bg-background-dark border-gray-300 dark:border-gray-600 group-hover:border-primary'
                }`} />
                <div>
                  <span className={`font-display text-4xl font-bold block leading-none mb-1 transition-all duration-500 ${
                    active === i ? 'text-primary' : 'text-gray-300 dark:text-gray-600 group-hover:text-gray-400'
                  }`}>
                    {item.year}
                  </span>
                  <span className={`text-xs uppercase tracking-widest transition-all duration-500 ${
                    active === i ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-600'
                  }`}>
                    {item.title}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right — scrollable image sections */}
        <div className="w-full lg:w-[65%]">
          {timelineItems.map((item, i) => (
            <div
              key={item.year}
              ref={(el) => (sectionRefs.current[i] = el)}
              className="relative overflow-hidden"
            >
              <img
                src={item.img}
                alt={item.title}
                className="w-full h-auto object-contain"
              />
              {/* Caption */}
              <div className="px-8 md:px-12 py-6 bg-background-light dark:bg-background-dark">
                <p className="text-[10px] uppercase tracking-[0.5em] text-primary mb-2">{item.year}</p>
                <h2 className="font-display text-3xl md:text-4xl dark:text-white mb-1">{item.title}</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{item.caption}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Founders */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="border-t border-b border-gray-200 dark:border-gray-700 py-4 mb-12 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.5em] text-gray-400">The People</span>
          <span className="font-display text-2xl md:text-3xl dark:text-white">Leaders</span>
          <span className="text-[10px] uppercase tracking-[0.5em] text-gray-400 hidden md:block">Est. 1905</span>
        </div>

        <div className="grid grid-cols-3 gap-4 md:gap-6">
          {founders.map((f) => (
            <div key={f.name} className="group">
              <div className="aspect-square md:aspect-[3/4] overflow-hidden mb-3">
                <img
                  src={f.img}
                  alt={f.name}
                  className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                />
              </div>
              <p className="text-[10px] uppercase tracking-widest text-primary mb-1">{f.role}</p>
              <h3 className="font-display text-sm md:text-xl dark:text-white">{f.name}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* Founder Quote */}
      <div className="bg-surface-dark text-white p-8 md:p-12 text-center">
        <div className="w-10 h-0.5 bg-primary mx-auto mb-8" />
        <p className="font-display text-2xl md:text-3xl italic mb-4">
          "Four Generations of Tailoring Excellence. One Modern Vision."
        </p>
        <p className="text-white/40 text-xs uppercase tracking-wider">
          Fourth-generation tailoring legacy leaders
        </p>
      </div>
    </>
  );
};

export default OurStory;
